/**
 * Add or edit a garment.
 *
 * Keyed by item id from the parent so switching targets remounts and re-seeds state —
 * the same bug that would have written one insurance quote's terms onto another's row.
 */
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import PhotoField from './PhotoField';
import type {
  GarmentCondition,
  GarmentPattern,
  Season,
  WardrobeCategory,
  WardrobeItem,
  WardrobePhoto,
} from '../../shared/types';
import { Button, Field, Select, TextInput, Textarea, colorHex, cx, errorText } from './ui';

const PATTERNS: GarmentPattern[] = ['solid', 'stripe', 'check', 'plaid', 'print', 'texture', 'other'];
const CONDITIONS: { value: GarmentCondition; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'good', label: 'Good' },
  { value: 'worn', label: 'Worn' },
  { value: 'retire', label: 'Retiring' },
];
const SEASONS: Season[] = ['spring', 'summer', 'autumn', 'winter'];

/** From the formality scale in migration 0014. Config in D1; labels mirrored here. */
const FORMALITY = [
  { value: 1, label: '1 — Yard work' },
  { value: 2, label: '2 — Casual' },
  { value: 3, label: '3 — Smart casual' },
  { value: 4, label: '4 — Business' },
  { value: 5, label: '5 — Formal' },
];

export default function ItemForm({
  item,
  categories,
  onSaved,
  onCancel,
  onPhotoChanged,
}: {
  item: WardrobeItem | null;
  categories: WardrobeCategory[];
  onSaved: () => void;
  onCancel: () => void;
  /**
   * A photo upload commits on its own, without the form being submitted, and hands back the
   * updated row. The parent patches it into the grid — deliberately NOT onSaved (which closes
   * the form) and NOT a refetch (which remounts it and loses edits in progress).
   */
  onPhotoChanged: (updated: WardrobeItem) => void;
}) {
  const [name, setName] = useState(item?.name ?? '');
  const [category, setCategory] = useState(item?.category ?? categories[0]?.slug ?? 'tee');
  const [colors, setColors] = useState((item?.colors ?? []).join(', '));
  const [pattern, setPattern] = useState<GarmentPattern>(item?.pattern ?? 'solid');
  const [formality, setFormality] = useState(item?.formality ?? 2);
  const [seasons, setSeasons] = useState<Season[]>(item?.seasons ?? []);
  const [fabric, setFabric] = useState(item?.fabric ?? '');
  const [brand, setBrand] = useState(item?.brand ?? '');
  const [size, setSize] = useState(item?.size ?? '');
  const [fitNotes, setFitNotes] = useState(item?.fit_notes ?? '');
  const [condition, setCondition] = useState<GarmentCondition>(item?.condition ?? 'good');
  const [notes, setNotes] = useState(item?.notes ?? '');
  const [sourcePhoto, setSourcePhoto] = useState(
    item?.source_photo_id === null || item?.source_photo_id === undefined
      ? ''
      : String(item.source_photo_id)
  );

  /**
   * Intake photos, for attributing this garment to the picture it was catalogued from.
   *
   * Loaded here rather than passed down because the list is short, rarely changes, and the form
   * is the only place it is needed. Failure is silent on purpose: not being able to offer the
   * attribution must never block saving a garment.
   */
  const [photos, setPhotos] = useState<WardrobePhoto[]>([]);
  useEffect(() => {
    let cancelled = false;
    api
      .get<WardrobePhoto[]>('/wardrobe/photos')
      .then((r) => !cancelled && setPhotos(r))
      .catch(() => {
        /* attribution is a nice-to-have; the form still works without it */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const colorList = colors
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  function toggleSeason(s: Season) {
    setSeasons((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (name.trim() === '') {
      setError('Give it a name you would actually say out loud — "tan corduroy trucker".');
      return;
    }

    const body = {
      name: name.trim(),
      category,
      colors: colorList,
      pattern,
      formality,
      seasons,
      fabric: fabric.trim() || null,
      brand: brand.trim() || null,
      size: size.trim() || null,
      fit_notes: fitNotes.trim() || null,
      condition,
      notes: notes.trim() || null,
      source_photo_id: sourcePhoto === '' ? null : Number(sourcePhoto),
    };

    setSaving(true);
    try {
      if (item) await api.put(`/wardrobe/items/${item.id}`, body);
      else await api.post('/wardrobe/items', body);
      onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!item) return;
    setSaving(true);
    try {
      await api.del(`/wardrobe/items/${item.id}`);
      onSaved();
    } catch (err) {
      setError(errorText(err));
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      {error && (
        <p role="alert" className="rounded-lg border border-clay/40 bg-clay-soft px-3 py-2 text-sm text-ink">
          {error}
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" hint="What you'd call it reaching for it.">
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            placeholder="tan corduroy trucker"
          />
        </Field>

        <Field label="Category">
          <Select value={category} onChange={(e) => setCategory(e.target.value)}>
            {categories.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Colours"
          hint="Comma separated, main one first. Plain words — olive, tan, cream."
        >
          <TextInput
            value={colors}
            onChange={(e) => setColors(e.target.value)}
            placeholder="tan, camel"
          />
        </Field>

        <Field label="Pattern">
          <Select value={pattern} onChange={(e) => setPattern(e.target.value as GarmentPattern)}>
            {PATTERNS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Formality" hint="What the outfit engine scores against the occasion.">
          <Select value={formality} onChange={(e) => setFormality(Number(e.target.value))}>
            {FORMALITY.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Condition">
          <Select
            value={condition}
            onChange={(e) => setCondition(e.target.value as GarmentCondition)}
          >
            {CONDITIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {/* Live swatch preview — the same colours the grid tile will draw, so a colour
          word that lands on nothing obvious is visible here rather than in the grid. */}
      {colorList.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-ink-soft">
          <span>Swatch:</span>
          {colorList.map((c) => {
            const hex = colorHex(c);
            return (
              <span key={c} className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block h-4 w-4 rounded border border-border"
                  style={{ backgroundColor: hex ?? 'var(--color-surface-sunk)' }}
                  aria-hidden="true"
                />
                <span className={cx(!hex && 'italic')}>{hex ? c : `${c} (no swatch)`}</span>
              </span>
            );
          })}
        </div>
      )}

      {/* A photo needs an id to attach to, so it is offered only once the row exists. Saying
          so is better than a disabled control with no explanation. */}
      {item ? (
        <PhotoField item={item} onUploaded={onPhotoChanged} />
      ) : (
        <p className="text-xs text-ink-soft">
          Add it to the wardrobe first, then reopen it to attach a photo.
        </p>
      )}

      <fieldset>
        <legend className="mb-2 text-sm font-medium text-ink">Seasons</legend>
        <div className="flex flex-wrap gap-2">
          {SEASONS.map((s) => {
            const on = seasons.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleSeason(s)}
                aria-pressed={on}
                className={cx(
                  'min-h-11 rounded-full border px-4 text-sm font-medium capitalize transition-colors',
                  'focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2',
                  'focus-visible:ring-offset-canvas focus-visible:outline-none',
                  on
                    ? 'border-sage bg-sage-soft text-sage'
                    : 'border-border bg-surface text-ink-soft hover:bg-surface-sunk hover:text-ink'
                )}
              >
                {s}
              </button>
            );
          })}
        </div>
        <p className="mt-1.5 text-xs text-ink-soft">
          Leave all off if it works year round. This is what keeps a corduroy trucker out of a
          July suggestion.
        </p>
      </fieldset>

      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Fabric">
          <TextInput value={fabric} onChange={(e) => setFabric(e.target.value)} placeholder="corduroy" />
        </Field>
        <Field label="Brand">
          <TextInput value={brand} onChange={(e) => setBrand(e.target.value)} />
        </Field>
        <Field label="Size">
          <TextInput value={size} onChange={(e) => setSize(e.target.value)} />
        </Field>
      </div>

      <Field label="Fit notes" hint="How the garment fits — never measurements of you.">
        <Textarea
          value={fitNotes}
          onChange={(e) => setFitNotes(e.target.value)}
          rows={2}
          placeholder="hits at the hip, sleeves roll well"
        />
      </Field>

      <Field label="Notes">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </Field>

      {/* Only offered once intake photos exist — an empty dropdown explains nothing. */}
      {photos.length > 0 && (
        <Field
          label="Catalogued from"
          hint="Which intake photo this came out of. It is how the Intake tab knows whether a photo has been fully worked through."
        >
          <Select value={sourcePhoto} onChange={(e) => setSourcePhoto(e.target.value)}>
            <option value="">Not from a photo</option>
            {photos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.caption ?? `Photo ${p.id}`}
                {p.items_found !== null && ` — ${p.linked_items}/${p.items_found} done`}
              </option>
            ))}
          </Select>
        </Field>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? 'Saving…' : item ? 'Save changes' : 'Add to wardrobe'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        {item && (
          <Button type="button" variant="danger" className="ml-auto" onClick={remove} disabled={saving}>
            Remove
          </Button>
        )}
      </div>
    </form>
  );
}
